import get from 'lodash/get'
import { mark } from '@pcln/measure'
import { isServerSide } from '@mfs/client/listings-client/util/isServerSide'
import { setSortOptions } from '@mfs/client/listings-client/reducers/sortOptions/sortOptions'
import { getSortOptionsState } from '@mfs/client/listings-client/util/getSortOptionsState'
import { getExdCarriersGraph } from '@mfs/client/listings-client/util/listingsHelpers'
import { setFlySearchResponseData } from '@mfs/client/listings-client/reducers/flySearchResponseData'
import { canLoadMoreitins } from '@mfs/client/listings-client/util/canLoadMoreitins'
import { setDisplayCarriers } from '@mfs/client/listings-client/reducers/airlines'
import { setListingsMetaData } from '@mfs/client/listings-client/reducers/listingsMetaData/listingsMetaData'
import { setHasExpressDeal } from '@mfs/client/listings-client/reducers/flags'
import { setSearchSessionKey } from '@mfs/client/listings-client/reducers/searchSessionKey/searchSessionKey'
import { setHasAppInstalled } from '@mfs/client/listings-client/reducers/hasAppInstalled'
import { setCheapestPrice } from '@mfs/client/listings-client/reducers/cheapestPrice/cheapestPrice'
import {
  setStandardSessionKey,
  setEmployeeSessionKey,
} from '@mfs/client/listings-client/reducers/filters/employeeDiscount/employeeDiscount'
import { PERF_EVENTS } from '@mfs/client/common/constants/perfEvents'
import updateListingsWithCheapestBadge from './updateListingsWithCheapestBadge'
import { setInitialFilterInfo } from '@mfs/client/listings-client/actions'
import { updateIncludedCarriers } from '@mfs/client/listings-client/reducers/filters/filterInfo/filterInfo'
import { logRetailPriceWithAPGData } from '@mfs/client/listings-client/util/logRetailPriceWithAPGData'
import {
  updateBrandReference,
  updateDisplayInfiniteScrollLoadMore,
  updateFetchedInitialListings,
  updateListingsFetchDataSuccess,
  updateListingsHasErrored,
  updateListingsIsLoading,
  updateRecommendedListings,
} from '@mfs/client/listings-client/reducers/listings'
import { setCheapestItinerary } from '@mfs/client/utils/setCheapestItinerary/setCheapestItinerary'

function logAndSetAirSearchMetadata(response, airSearchRequestModel) {
  airSearchRequestModel.setSearchId({
    searchId: response.listingsMetaData.searchId,
  })
  airSearchRequestModel.setSearchSessionKey({
    searchSessionKey: response.listingsMetaData.searchSessionKey,
  })
}

export function processAirSearchSuccessResponse({ res, dispatch, state }) {
  const { activeTabId, pclnBootstrapData, airSearchRequestModel } = state

  const { response } = res

  const {
    airports = {},
    airlines = {},
    listingsMetaData = {},
    filterDefaults = {},
    brandReference = [],
    priceGuidance = {},
    displayableCarriers,
    recommendedListings,
    hasAppInstalled,
  } = response

  const {
    searchSessionKey,
    clientSessionId,
    hasExpressDeal,
    tripType,
    totalFilteredItineraries,
    upperBound,
    minimumRetailPrice,
  } = listingsMetaData

  const { searchRequest } = pclnBootstrapData

  dispatch(setSortOptions(getSortOptionsState(searchRequest?.slices?.length, filterDefaults.SORT_OPTIONS)))

  /**
   * Set the Initial STATIC Data here before dispatching any redux events
   * */

  let exdCarriers = []

  if (displayableCarriers) {
    exdCarriers = getExdCarriersGraph(displayableCarriers, airlines)
  }

  const filterableSliceData = {}

  filterDefaults?.TIME?.forEach((sliceSummary) => {
    filterableSliceData[sliceSummary.sliceRefId] = sliceSummary
  })

  const initialSearchIncludedCarriers = airSearchRequestModel.filter?.sliceFilters?.[0]?.includedCarriers
  filterDefaults.AIRLINES = filterDefaults.AIRLINES.map((slice) => ({
    ...slice,
    checked: initialSearchIncludedCarriers?.length > 0,
    disabled: initialSearchIncludedCarriers?.length === 1,
  }))

  filterDefaults.AIRPORTS = filterDefaults.AIRPORTS.map((slice) => {
    slice.destinationAirports = slice.destinationAirports.map((i) => ({ ...i, checked: false }))
    slice.originAirports = slice.originAirports.map((i) => ({ ...i, checked: false }))
    return slice
  })

  // Singleton to store initial response objects
  const flySearchResponseData = {
    filters: [],
    airports,
    airlines,
    searchSessionKey,
    clientSessionId,
    filterDefaults,
    filterableSliceData,
    priceGuidance,
    exdCarriers, // TODO: Merge this node with EXD Response node
  }
  dispatch(setFlySearchResponseData(flySearchResponseData))

  // we have fetched all that we can fetch, don't fetch any more
  const loadMore = canLoadMoreitins(upperBound, totalFilteredItineraries)

  // handle results - start
  logAndSetAirSearchMetadata(response, airSearchRequestModel)
  // handle results - end

  /**
   * TODO: Refactor this abomination after the Graph Win
   */
  const listingsObj = {
    response: response.listings,
  }

  listingsObj.response = updateListingsWithCheapestBadge({ response })

  // add isCheapest to the listings
  listingsObj.response = setCheapestItinerary(listingsObj.response)
  let processedRecommendedListings = recommendedListings
  if (recommendedListings) {
    processedRecommendedListings = setCheapestItinerary(recommendedListings)
  }

  const cheapestFromListings = listingsObj.response.find((i) => i.isCheapest)?.price?.[1]?.amount
  const cheapestFromRecommendedListings = processedRecommendedListings?.find((i) => i.isCheapest)?.price?.[1]
    ?.amount
  const prices = [cheapestFromListings, cheapestFromRecommendedListings].filter((p) => p != null)
  const cheapestPrice = prices.length > 0 ? Math.min(...prices) : undefined

  dispatch(setCheapestPrice({ sliceId: 0, cheapestPrice: cheapestPrice }))

  // Process recommended listings with badges
  if (recommendedListings && recommendedListings.length > 0) {
    processedRecommendedListings = updateListingsWithCheapestBadge({
      response: { listings: processedRecommendedListings, listingsMetaData },
      isRecommendedListings: true,
    })
  }

  // adds inline ad to recommended listings after hydration
  if (
    !pclnBootstrapData?.isMobile &&
    processedRecommendedListings?.length > 0 &&
    pclnBootstrapData?.isNotReshopAndWLAndPackages
  ) {
    processedRecommendedListings = [
      ...processedRecommendedListings,
      { isSponsoredAd: true, adId: 'div-gpt-ad-1774275433713-0' },
    ]
  }

  dispatch(updateListingsIsLoading(false))
  // TODO: Not needed for Redux state - REfactor this
  dispatch(setDisplayCarriers(displayableCarriers || []))
  dispatch(setListingsMetaData(get(response, 'listingsMetaData', null)))
  /**
   * TODO: Refactor this abomination after the Graph Win
   */
  dispatch(setHasExpressDeal(hasExpressDeal))
  if (brandReference) {
    dispatch(updateBrandReference(brandReference))
  }
  dispatch(updateDisplayInfiniteScrollLoadMore({ loadMore, activeTabId }))
  dispatch(updateListingsFetchDataSuccess(listingsObj))
  dispatch(updateRecommendedListings({ recommendedListings: processedRecommendedListings, activeTabId }))
  dispatch(updateFetchedInitialListings(true))
  dispatch(updateListingsHasErrored(false))
  dispatch(setSearchSessionKey(searchSessionKey))
  dispatch(setHasAppInstalled(hasAppInstalled))

  // Store session key based on employee discount toggle state
  const isEmployeeDiscountEnabled = state.employeeDiscount?.isEnabled

  if (isEmployeeDiscountEnabled) {
    // Store as employee session key (cache it for future use)
    dispatch(setEmployeeSessionKey(searchSessionKey))
  } else {
    // Store as standard session key (cache it for future use)
    dispatch(setStandardSessionKey(searchSessionKey))
  }

  if (!isServerSide) {
    mark(PERF_EVENTS.transformGraphAirSearchRspEnd)
  }

  // Set Filter Defaults
  dispatch(setInitialFilterInfo({ payload: filterDefaults, sbsParams: { activeTabId } }))
  if (get(pclnBootstrapData, 'searchRequest.params[included-airlines]', '')) {
    dispatch(updateIncludedCarriers(get(pclnBootstrapData, 'searchRequest.params[included-airlines]', '')))
  }

  /**
   * Log APG minimum price and minimum retail price
   * */
  if (!isServerSide) {
    logRetailPriceWithAPGData({
      searchRequest,
      minimumRetailPrice,
      tripType,
    })
  }
  return null
}
